// Must evaluate before any module constructs a Zod schema, or the compiled fast path never installs.
import 'zod/compile';
import * as Sentry from '@sentry/react';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import './index.css';
import App from './App.tsx';
import { initPostHog } from '@/lib/posthog';
import { captureAuthToken } from '@/lib/api';
import { captureAuthLinkOutcome } from '@/lib/auth-link-return';
import { initErrorReporter } from '@/lib/error-reporter';
import { SentryWebErrorReporter } from '@/lib/error-reporters/sentry';
import { installPreloadErrorRecovery } from '@/lib/preloadErrorRecovery';
import {
  hasCapabilityUrlSecret,
  scrubSensitiveSentryData,
  scrubSentryBreadcrumb,
  scrubSentryReplayEvent,
  scrubSentryReplayRecordingEvent,
  scrubSentrySpan,
  scrubSentryTransaction,
  stripUrlSecrets,
} from '@reflex/shared/sentry-scrub';

installPreloadErrorRecovery();

const hostname = window.location.hostname;
const isLocalhost = hostname === 'localhost' || hostname === '127.0.0.1';

function getSentryEnvironment(): string {
  if (isLocalhost) return 'development';
  if (hostname.includes('staging')) return 'staging';
  return 'production';
}

const sentryDsn = import.meta.env.VITE_SENTRY_DSN;
const hasCapabilityUrl = hasCapabilityUrlSecret(window.location.href);

// The sign-in redirect lands with the session token in the URL fragment.
// Strip it before any telemetry client can observe `location.href`. This is
// the control that keeps the token out of replay frames, breadcrumbs, and
// PostHog — the scrubbing below is defense in depth for ordinary query
// strings, not for credentials, which must never be in the address bar once
// telemetry is running.
captureAuthToken();
// A full-page account link lands on the app root with its outcome in the
// query; restore the page the user started from before the router reads it.
captureAuthLinkOutcome();

if (sentryDsn) {
  // rrweb's Meta frames read `location.href` directly and do not pass
  // through Replay's recording callback. Give synchronous Sentry/Replay
  // setup a query-free URL for the *first* frame, then restore the real
  // address with the native history method captured before Sentry
  // instruments it. This does not cover later Meta frames: Replay takes a
  // fresh full snapshot every 60s in buffer mode and whenever it restarts
  // recording after an error, and each reads the live URL. Query strings on
  // app routes (search, filters) can therefore appear in a replay; secrets
  // cannot, because `captureAuthToken` above already removed the only one.
  const originalHref = window.location.href;
  const scrubbedHref = stripUrlSecrets(originalHref);
  const nativeReplaceState = window.history.replaceState.bind(window.history);
  if (scrubbedHref !== originalHref) {
    nativeReplaceState(window.history.state, '', scrubbedHref);
  }

  try {
    Sentry.init({
      dsn: sentryDsn,
      enabled: !isLocalhost,
      // Never let the SDK collect cookies, credentials, request bodies, or IP
      // addresses on its own. `beforeSend` / `beforeBreadcrumb` scrub what
      // reaches an event by other routes; see `@reflex/shared/sentry-scrub`.
      sendDefaultPii: false,
      release: __SENTRY_RELEASE__ || undefined,
      environment: import.meta.env.VITE_SENTRY_ENVIRONMENT ?? getSentryEnvironment(),
      integrations: [
        Sentry.browserTracingIntegration(),
        // Replay envelopes bypass beforeSend. Its event processor scrubs the
        // initial and navigated URL metadata immediately before serialization.
        ...(hasCapabilityUrl
          ? []
          : [
              {
                name: 'ReflexReplayPrivacy',
                processEvent: scrubSentryReplayEvent,
              },
              // rrweb Meta frames bypass every Sentry callback and read the
              // live address. Do not start Replay on a capability URL.
              Sentry.replayIntegration({
                maskAllText: true,
                maskAllInputs: true,
                blockAllMedia: true,
                networkDetailAllowUrls: [],
                networkCaptureBodies: false,
                beforeAddRecordingEvent: scrubSentryReplayRecordingEvent,
              }),
            ]),
      ],
      tracesSampleRate: 1.0,
      replaysSessionSampleRate: hasCapabilityUrl ? 0 : 0.1,
      replaysOnErrorSampleRate: hasCapabilityUrl ? 0 : 1.0,
      beforeSend: scrubSensitiveSentryData,
      beforeSendTransaction: scrubSentryTransaction,
      beforeSendSpan: scrubSentrySpan,
      beforeBreadcrumb: scrubSentryBreadcrumb,
      ignoreErrors: [
        /Failed to fetch dynamically imported module/i,
        /Importing a module script failed/i,
        /'text\/html' is not a valid JavaScript MIME type/i,
      ],
    });
  } finally {
    if (scrubbedHref !== originalHref) {
      nativeReplaceState(window.history.state, '', originalHref);
    }
  }
} else if (!isLocalhost) {
  console.warn('[sentry] VITE_SENTRY_DSN not set; error reporting disabled');
}

initErrorReporter(new SentryWebErrorReporter());

initPostHog();

if (isLocalhost) {
  document.title = '[local] Reflex';
  //scan({ enabled: true });
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <App />
  </StrictMode>,
);
